import { animated } from '@react-spring/web'
import { useCallback, useContext, useMemo } from 'react'

import { type SanityFrameBuilderGlass } from '@frame-builder/data/sanity/queries/types/glass'
import { FrameBuilderSettingsContext } from '@frame-builder/contexts/settings-context'
import {
  FrameBuilderCommittedStateContext,
  FrameBuilderSelectedStateContext,
} from '@frame-builder/contexts/state-context'
import { FrameBuilderStringsContext } from '@frame-builder/contexts/strings-context'
import { FrameBuilderValidationContext } from '@frame-builder/contexts/validation-context'
import { getItemTitle, toRecord } from '@frame-builder/lib/helpers'
import { type ItemPropsWithId } from '@frame-builder/lib/item'
import { useBlinkingState } from '@frame-builder/lib/preview-animation'
import {
  getStatePrice,
  getStatePriceWithDiscount,
} from '@frame-builder/lib/state-price'
import { type StepDefinitionProps } from '@frame-builder/lib/steps'

import Button from '@frame-builder/components/utility/button'
import ItemImage from '@frame-builder/components/utility/item-image'
import ItemList from '@frame-builder/components/utility/item-list'
import ListContainer from '@frame-builder/components/utility/list-container'
import PriceBadge from '@frame-builder/components/utility/price-badge'
import ConfigurationDefinition from './configuration-definition'

const GlassDefinition = ({
  stepNumber,
  onClose,
  className,
}: StepDefinitionProps) => {
  const { committedState } = useContext(FrameBuilderCommittedStateContext)
  const { selectedState, dispatchSelectedState } = useContext(
    FrameBuilderSelectedStateContext,
  )
  const { frameBuilderConfiguration, frameBuilderType } = useContext(
    FrameBuilderSettingsContext,
  )
  const frameBuilderStrings = useContext(FrameBuilderStringsContext)
  const { validationErrorMessages } = useContext(FrameBuilderValidationContext)

  const [opacity, setIsBlinking] = useBlinkingState()

  // Calculate frame prices for comparison
  const framePrices = useMemo(() => {
    return toRecord<number>(
      frameBuilderConfiguration.glass.map((glass) => {
        return {
          [glass._id]: getStatePrice(
            frameBuilderType,
            frameBuilderConfiguration,
            {
              ...committedState,
              glass,
            },
          ),
        }
      }),
    )
  }, [committedState, frameBuilderConfiguration, frameBuilderType])
  const framePricesWithDiscount = useMemo(() => {
    return toRecord<number>(
      frameBuilderConfiguration.glass.map((glass) => {
        return {
          [glass._id]: getStatePriceWithDiscount(
            frameBuilderType,
            frameBuilderConfiguration,
            {
              ...committedState,
              glass,
            },
          ),
        }
      }),
    )
  }, [committedState, frameBuilderConfiguration, frameBuilderType])

  const selectGlass = useCallback(
    (glass: SanityFrameBuilderGlass) => {
      dispatchSelectedState({
        type: 'glass',
        glass,
      })
    },
    [dispatchSelectedState],
  )
  const handleChange = useCallback(
    (id: string) => {
      const newGlass = frameBuilderConfiguration.glass.find(
        (glass) => glass._id === id,
      )

      if (newGlass) {
        selectGlass(newGlass)
      }
    },
    [frameBuilderConfiguration, selectGlass],
  )

  const isValidSelection = useMemo(() => {
    return !validationErrorMessages?.glassTypePreview
  }, [validationErrorMessages])

  const handleOkClick = useCallback(() => {
    if (isValidSelection) {
      onClose()
    }

    if (!isValidSelection) {
      setIsBlinking(true)
    }
  }, [isValidSelection, onClose, setIsBlinking])

  return (
    <ConfigurationDefinition
      stepNumber={stepNumber}
      title={frameBuilderStrings.glassStepTitle}
      footerContent={frameBuilderStrings.glassStepFooter}
      onClose={onClose}
      className={className}
    >
      <div className="flex flex-col px-5 space-y-5">
        <animated.div
          style={{
            opacity,
          }}
        >
          <ListContainer gapSize="sm">
            <ItemList
              defaultValue={selectedState.glass?._id}
              onChange={handleChange}
              items={frameBuilderConfiguration.glass.map((glass) => {
                const title = getItemTitle(
                  frameBuilderStrings,
                  glass.title ?? '',
                  glass.discountPercent,
                )

                const imageElement = (
                  <ItemImage image={glass.icon} alt={title} />
                )
                let detailsElement: JSX.Element | undefined
                let badgeElement: JSX.Element | undefined

                if (
                  selectedState.glass &&
                  selectedState.glass._id !== glass._id
                ) {
                  badgeElement = (
                    <>
                      <PriceBadge
                        price={
                          framePricesWithDiscount[glass._id] -
                          framePricesWithDiscount[selectedState.glass._id]
                        }
                        showPlusSign
                      />
                      {!!glass.discountPercent && (
                        <PriceBadge
                          price={
                            framePricesWithDiscount[glass._id] -
                            framePrices[glass._id]
                          }
                          showPlusSign
                          disabled
                          className="ml-1"
                        />
                      )}
                    </>
                  )
                }

                if (glass.description) {
                  detailsElement = (
                    <div className="w-full pt-2.5 pb-4 px-2.5 space-y-4">
                      <div className="flex text-xs border-t pt-2.5">
                        {glass.description}
                      </div>
                    </div>
                  )
                }

                const item: ItemPropsWithId = {
                  id: glass._id,
                  title,
                  imageElement,
                  detailsElement,
                  badgeElement,
                }

                return item
              })}
            />
          </ListContainer>
        </animated.div>

        <div>
          <Button
            size="small"
            isVisuallyDisabled={!isValidSelection}
            onClick={handleOkClick}
            className="min-w-32 grow-0 shrink-0"
          >
            {frameBuilderStrings.controlOkLabel}
          </Button>
        </div>
      </div>
    </ConfigurationDefinition>
  )
}

export default GlassDefinition
